import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MailCheck, KeyRound, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

// New staff arriving from their ID card QR set up their account here.
// The link is always emailed to the address HR has on file, never typed by the visitor.
export default function Activate() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [error, setError] = useState("");
    const [sending, setSending] = useState(false);
    const [sentTo, setSentTo] = useState("");

    useEffect(() => {
        api.get(`/public/staff/${encodeURIComponent(token)}/activation`)
            .then(({ data }) => setInfo(data))
            .catch((e) => setError(formatApiError(e.response?.data?.detail) || "This link is not valid"));
    }, [token]);

    const send = async () => {
        setSending(true);
        setError("");
        try {
            const { data } = await api.post(`/public/staff/${encodeURIComponent(token)}/send-activation`);
            setSentTo(data.sent_to);
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || "Could not send the email");
        } finally {
            setSending(false);
        }
    };

    const loginLink = `/login?audience=employee${info?.company_id ? `&company=${info.company_id}` : ""}`;

    return (
        <div className="min-h-screen bg-bone flex items-center justify-center p-5" data-testid="activate-page">
            <div className="w-full max-w-md bg-white border border-ink/10 p-7 sm:p-9">
                {info && (
                    <div className="flex items-center gap-3" data-testid="activate-company">
                        {info.company_logo ? (
                            <img src={info.company_logo} alt="" className="w-12 h-12 object-contain rounded-xl" />
                        ) : (
                            <div className="w-12 h-12 rounded-xl bg-klein flex items-center justify-center text-white font-display font-extrabold text-xl">
                                {(info.company_name || "H")[0].toUpperCase()}
                            </div>
                        )}
                        <div className="font-display font-bold text-ink">{info.company_name}</div>
                    </div>
                )}
                <div className="mt-7 w-12 h-12 rounded-2xl bg-klein-50 text-klein flex items-center justify-center">
                    {sentTo ? <MailCheck className="w-6 h-6" /> : <KeyRound className="w-6 h-6" />}
                </div>
                <h1 className="mt-4 font-display font-extrabold text-3xl tracking-[-0.03em] text-ink">
                    {sentTo ? "Check your email" : info && !info.needs_activation ? "You're all set" : `Welcome${info?.first_name ? `, ${info.first_name}` : ""}`}
                </h1>
                {!sentTo && info && !info.needs_activation && (
                    <p className="mt-2 text-sm text-ink/65" data-testid="activate-already-active">
                        Your account is already active. Sign in with your staff ID and password.
                    </p>
                )}
                {!sentTo && info && info.needs_activation && (
                    <>
                        <p className="mt-2 text-sm text-ink/65">
                            Your staff account ({info.employee_no}) isn&apos;t activated yet. We&apos;ll email a secure link to
                            {" "}<b className="text-ink" data-testid="activate-email-masked">{info.email_masked || "your email on file"}</b>{" "}
                            so you can set your password.
                        </p>
                        <button onClick={send} disabled={sending || !info.email_masked || !info.active}
                            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-klein text-white py-3.5 text-sm hover:bg-klein-700 disabled:opacity-60"
                            data-testid="activate-send-button">
                            {sending ? "Sending…" : "Email me my activation link"} <ArrowRight className="w-4 h-4" />
                        </button>
                        {!info.email_masked && <p className="mt-3 text-sm text-amber-700" data-testid="activate-no-email">No email on file — please ask HR to add your email.</p>}
                    </>
                )}
                {sentTo && (
                    <p className="mt-2 text-sm text-ink/65" data-testid="activate-sent">
                        We sent your activation link to <b className="text-ink">{sentTo}</b>. Open it to set your password, then sign in.
                    </p>
                )}
                {error && <p className="mt-4 text-sm text-red-600" data-testid="activate-error">{error}</p>}
                <Link to={loginLink} className="mt-6 inline-block text-sm text-klein hover:underline" data-testid="activate-login-link">
                    Already activated? Sign in
                </Link>
            </div>
        </div>
    );
}
